import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ICON_LARGE_SIZE, ICON_SIZE, ICON_SMALL_SIZE } from '../constants';
import IconDefault from '../SvgIcon/IconDefault';
import { ADVENTURE_GREEN, ADVENTURE_GREEN_FOCUSED, ELEMENT_GRAY } from '../colors';
import { useDeviceType } from '../hooks';
import { isDefined } from '../helpers/defined';
import { absoluteCenteredStyle } from '../tokens/centered';
import { combineByBreakpoints } from '../helpers/media-combine';
import { buttonIconsColorKeys, buttonIconsColorMixin } from './style/icons-color';
import { buttonTransitionStyle } from './style/transition';
import { userSelectNoneStyle } from '../tokens/user-select';
import { appearanceNoneStyle } from '../tokens/appearance';
import { baseHighlightStyle } from '../tokens/highlight';
import { setNoFocus } from '../helpers/events-focus';
import { withOmitProps } from '../helpers/elements';
import { activePressedStyle } from '../tokens/pressed';
import { size } from '../style/size';
const StyledClickableArea = styled.div`
  ${absoluteCenteredStyle}
  ${({
  $size
}) => isDefined($size) ? size($size) : ''}
`;
const ButtonAsIcon = styled.button.withConfig(withOmitProps([...buttonIconsColorKeys, 'hasTouch', 'inactive', 'noAnimate', 'withSuffix', 'withPrefix', 'vertical']))`
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  border: none;
  background-color: transparent;
  cursor: ${({
  disabled,
  inactive
}) => disabled || inactive ? 'default' : 'pointer'};
  outline: none;
  margin: 0;
  padding: 0;
  ${buttonTransitionStyle}
  ${baseHighlightStyle}
  ${userSelectNoneStyle}
  ${appearanceNoneStyle}
  ${({
  inactive,
  disabled,
  $noTransform
}) => !inactive && !disabled && !$noTransform && activePressedStyle}
  ${buttonIconsColorMixin}
  flex-shrink: 0;
  flex-grow: 0;
  ${({
  $size
}) => isDefined($size) ? size($size) : ''}
  & > * {
    ${absoluteCenteredStyle}
  }
`;
const IconButton = /*#__PURE__*/React.forwardRef(({
  as,
  forwardedAs,
  disabled = false,
  inactive = false,
  asDiv = false,
  asSpan = false,
  hasArrow = false,
  hasClose = false,
  noIcon = false,
  iconComponent: IconComponent = IconDefault,
  icon,
  iconSize,
  iconColor = ELEMENT_GRAY,
  iconColorHover = ADVENTURE_GREEN,
  iconColorActive = ADVENTURE_GREEN_FOCUSED,
  iconColorFocus = ADVENTURE_GREEN,
  iconColorDisabled = ELEMENT_GRAY,
  arrow = 'right',
  size = ICON_SIZE,
  outerSize,
  sizeVariant = 'medium',
  noFocus = false,
  noTransform = false,
  ...props
}, forwardedRef) => {
  const {
    hasTouchScreen
  } = useDeviceType();
  const iconComponentSize = useMemo(() => /* prettier-ignore */
  iconSize || (typeof sizeVariant === 'string' ? hasArrow ? sizeVariant === 'tiny' ? ICON_SIZE : ICON_LARGE_SIZE : sizeVariant === 'tiny' ? ICON_SMALL_SIZE : ICON_SIZE : hasArrow ? combineByBreakpoints([sizeVariant, ICON_SIZE, ICON_LARGE_SIZE], ([sizeVariant, sizeTiny, sizeDefault]) => sizeVariant === 'tiny' ? sizeTiny : sizeDefault) : combineByBreakpoints([sizeVariant, ICON_SMALL_SIZE, ICON_SIZE], ([sizeVariant, sizeTiny, sizeDefault]) => sizeVariant === 'tiny' ? sizeTiny : sizeDefault)), [iconSize, hasArrow, sizeVariant]);
  return /*#__PURE__*/React.createElement(ButtonAsIcon, _extends({
    as: forwardedAs ?? as ?? (props.href ? 'a' : asDiv ? 'div' : asSpan ? 'span' : undefined),
    ref: forwardedRef,
    onTouchStart: () => {},
    disabled: disabled,
    inactive: inactive,
    iconColor: iconColor,
    hoverIconColor: iconColorHover,
    activeIconColor: iconColorActive,
    focusIconColor: iconColorFocus,
    disabledIconColor: iconColorDisabled,
    $noTransform: noTransform,
    $hasTouch: hasTouchScreen,
    $size: size
  }, props, setNoFocus(props, noFocus)), outerSize && /*#__PURE__*/React.createElement(StyledClickableArea, {
    $size: outerSize
  }), !noIcon && /*#__PURE__*/React.createElement(IconComponent, {
    disabled: disabled,
    hasArrow: hasArrow,
    hasClose: hasClose,
    arrow: arrow,
    size: iconComponentSize,
    icon: icon,
    color: null,
    colorHover: null
  }));
});
export default IconButton;